import { JSX } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { numeric } from './styles/dashboard-tokens';

/**
 * Shared chrome for the two "needs attention" list cards, so they cannot drift apart.
 */

type NewItemsBadgeProps = {
    total: number,
}

/**
 * Replaces the 30px solid-green circle that used to sit in these card headers. That dot was the
 * loudest element on the dashboard and carried exactly one bit of information; this says how many
 * and stays quiet while doing it. The dot survives at 8px as the live indicator.
 */
export const NewItemsBadge: (props: NewItemsBadgeProps) => JSX.Element | null = (
    { total }: NewItemsBadgeProps
): JSX.Element | null => {
    if (!total || total <= 0) {
        return (
            <Typography variant="body2" color="text.disabled">{ "All clear" }</Typography>
        );
    }
    return (
        <Box
            sx={ {
                display: 'flex',
                alignItems: 'center',
                gap: 0.875,
                pl: 1,
                pr: 1.25,
                py: 0.5,
                borderRadius: 5,
                color: 'success.main',
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.12),
            } }
        >
            { /* `color` above is what the .pulser rule inherits for both dot and halo. */ }
            <div className="pulser" />
            <Typography component="span" sx={ { ...numeric, fontSize: 12, fontWeight: 700 } }>
                { total + " new" }
            </Typography>
        </Box>
    );
}

type ViewAllLinkProps = {
    to: string,
    label: string,
}

/** Footer link out to the full resource, since the card itself no longer paginates. */
export const ViewAllLink: (props: ViewAllLinkProps) => JSX.Element = (
    { to, label }: ViewAllLinkProps
): JSX.Element => {
    return (
        <Box sx={ { mt: 'auto', pt: 1.5, borderTop: 1, borderColor: 'divider' } }>
            <Typography
                component={ RouterLink }
                to={ to }
                variant="body2"
                sx={ {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontWeight: 600,
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                } }
            >
                { label }
                <ArrowForwardIcon sx={ { fontSize: 14 } } />
            </Typography>
        </Box>
    );
}

/**
 * Density and weight for a React Admin <Datagrid> sitting inside a dashboard card. The default
 * table is tuned for a full-page list: too tall, and its header competes with the card title.
 */
/**
 * Share a fixed-layout table's width between its columns.
 *
 * Under `table-layout: fixed` every column that names no width takes an equal share, which
 * gives a status column as much room as an email address. Pass one entry per column, in
 * order; anything CSS accepts as a width works, so `64px` pins the id and `30%` gives the
 * email a real share of whatever is left.
 *
 * @param widths one per column, left to right
 */
export function dashboardColumnWidths(widths: string[]): Record<string, object> {
    const sx: Record<string, object> = {};
    widths.forEach((width: string, index: number): void => {
        const nth: number = index + 1;
        sx[`& .RaDatagrid-headerCell:nth-of-type(${nth}), & .RaDatagrid-rowCell:nth-of-type(${nth})`] = { width };
    });

    return sx;
}

export const dashboardListSx = {
    '& .RaList-content': {
        boxShadow: 'none',
        bgcolor: 'transparent',
    },
    // HQ-09. `table-layout: auto` sized each column to its own content, so the table claimed
    // 760px whatever the card was given and the right-hand columns sat off screen behind a
    // scrollbar - on any display narrower than 1920, which is most of them. Fixed layout
    // makes the columns share the width the card actually has, so every one of them stays on
    // screen and the values give way instead. Stacking the cards full width was the other
    // way and was rejected: the cards only fit side by side at 1920, so stacking was the
    // layout nearly all the time.
    // The class is doubled deliberately. React-admin ships
    // `.RaDatagrid-root .RaDatagrid-table { table-layout: auto }`, which is two classes and
    // is injected after this, so a single-class rule here ties on specificity and loses.
    '& .RaDatagrid-table.RaDatagrid-table': {
        tableLayout: 'fixed',
        width: '100%',
    },
    '& .RaDatagrid-headerCell': {
        bgcolor: 'transparent',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        py: 1,
        // The header has to give way on the same terms as the cell below it, or a long
        // column title sets a width the data never needed.
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    // Rather than clip a control mid-word (the Edit button rendered as "ED") or push columns
    // out of view, long values ellipsize and every column stays on screen. The full value is
    // one click away on the record itself.
    '& .RaDatagrid-rowCell': {
        fontSize: 13,
        py: 0.875,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    // The row actions column must never be the one that gets squeezed.
    '& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type': {
        overflow: 'visible',
    },
    '& .MuiTablePagination-root': {
        display: 'none',
    },
};
